<template>
  <FormSubmitPopup
    :title="isReadOnly ? $t('auditDetail') : $t('editAudit')"
    v-model="value"
    @popupClose="$emit('input', false)"
    :rightMethod="onSubmitted"
    :rightButtonText="$t('save')"
    :rightButtonDisabled="isLoading"
    :hideRgtBtn="isReadOnly"
    hasAlert
    large
  >
    <template v-slot:input>
      <v-container fluid>
        <v-alert
          v-model="showSuccessAlert"
          dismissible
          type="success">{{alertMsg}}</v-alert>
        <v-layout align-center>
          <v-flex xs3>
            <v-text-field
              append-icon="filter_list"
              class="text-xs-right"
              :label="$t('search')"
              single-line
              clearable
              hide-details
              v-model="filter"
            ></v-text-field>
          </v-flex>
          <v-flex class="ml-1" xs3>{{`Updated: ${lastUpdated}`}}</v-flex>
          <v-flex xs1>
            <v-checkbox :label="$t('unmatched')" v-model="showUnmatched"></v-checkbox>
          </v-flex>
          <v-flex xs1></v-flex>
          <v-flex xs1>
            <v-checkbox :label="$t('confirmed')" v-model="showConfirmed"></v-checkbox>
          </v-flex>
          <v-flex xs1></v-flex>
          <v-flex xs1 v-if="!isReadOnly">
            <LoaderButton
              v-model="isLoading"
              :buttonText="$t('syncInventory')"
              :promiseAwait="syncInventory"
            ></LoaderButton>
          </v-flex>
          <v-flex xs1>
            <v-btn @click="exportAuditProducts" dark color="primary">
              {{ $t('export') }}
            </v-btn>
          </v-flex>
        </v-layout>
        <v-layout justify-start align-baseline>
          <v-flex class="success--text caption"><span class="realtime-border px-1">{{ $t('realTIME') }}</span></v-flex>
        </v-layout>
        <v-data-table
          :headers="headers"
          class="elevation-2 myDense"
          :search="filter"
          :items="items"
          item-key="key"
          :rows-per-page-items="[25, 50, 100,  {text: $t('all'), value: -1}]"
        >
          <template v-slot:items="props">
            <td class="text-xs-left">{{ props.item.upc }}</td>
            <td class="text-xs-left">{{ upcToProductMap[props.item.upc] && upcToProductMap[props.item.upc].description }}</td>
            <td class="text-xs-left">{{ props.item.qty }} / {{props.item.abnormalQty || 0}}</td>
            <td class="text-xs-left">
              <v-layout justify-space-between v-if="!isReadOnly">
                <v-flex xs5>
                  <v-text-field
                    single-line
                    hide-details
                    v-model.number="props.item.auditQty"
                    :rules="[fieldIsNumber()]"
                  >
                  <template v-slot:append v-if="props.item.auditQty !== props.item.qty">
                    <div :class="getTextClass(props.item.auditQty, props.item.qty, props.item.isConfirmed)" style="min-width: 30px;">{{props.item.auditQty - props.item.qty}}</div>
                  </template>
                  </v-text-field>
                </v-flex>
                <v-flex xs5>
                  <v-text-field
                    single-line
                    hide-details
                    v-if="!isReadOnly"
                    v-model.number="props.item.abnormalAuditQty"
                    :rules="[fieldIsNumber()]"
                  > 
                    <template v-slot:append v-if="props.item.abnormalAuditQty !== props.item.abnormalQty">
                      <div :class="getTextClass(props.item.abnormalAuditQty, props.item.abnormalQty, props.item.isConfirmed)" style="min-width: 20px;">{{props.item.abnormalAuditQty - props.item.abnormalQty}}</div>
                    </template>
                  </v-text-field>
                </v-flex>
              </v-layout>
              <v-layout v-else>
                <span :style="getTextStyle(props.item.auditQty, props.item.qty, props.item.isConfirmed)">
                  {{ props.item.auditQty }}
                </span>/
                <span :style="getTextStyle(props.item.abnormalAuditQty, props.item.abnormalQty, props.item.isConfirmed)">
                  {{props.item.abnormalAuditQty}}
                </span>
              </v-layout>
            </td>
            <td class="text-xs-center">
              <v-layout justify-center align-baseline>
                <v-flex xs1>
                  <v-checkbox 
                    v-model="props.item.isConfirmed" 
                    style="position: relative; left: -5px; top: 15px;"
                    v-if="!isReadOnly"
                  ></v-checkbox>
                  <v-icon v-else-if="props.item.isConfirmed">check_circle</v-icon>
                </v-flex>
              </v-layout>
            </td>
            <td class="text-xs-left">
              <v-layout>{{ upcToProductMap[props.item.upc] && upcToProductMap[props.item.upc].location && ((upcToProductMap[props.item.upc].location.find(item => item.siteKey === initRecord.siteKey) || {}).location || []).join(', ') }}</v-layout>
            </td>
            <td class="text-xs-center">
              <v-text-field
                class="table-item"
                single-line
                hide-details
                v-model="props.item.note"
                v-if="!isReadOnly"
              ></v-text-field>
              <v-layout v-else>{{ props.item.note }}</v-layout>
            </td>
          </template>
        </v-data-table>
      </v-container>
    </template>
  </FormSubmitPopup>
</template> 

<script>
import FormSubmitPopup from './FormSubmitPopup'
import LoaderButton from './LoaderButton'
import { checkRules, cloneDeep, toPickerDateString } from '@/utils/tools'
import xlsx from 'xlsx'

export default {
  name: 'WarehouseAuditEditPopup',
  components: {
    FormSubmitPopup,
    LoaderButton
  },
  mixins: [checkRules],
  data () {
    return {
      filter: '',
      headers: [
        { text: this.$t('upc'), align: 'left', sortable: false, value: 'upc' },
        { text: this.$t('productName'), align: 'left', sortable: false, value: 'keywordString' },
        { text: this.$t('qtyNormalAbnormal'), value: 'qty', align: 'left', sortable: false },
        { text: this.$t('auditQtyNormalAbnormal'), value: 'auditQty', align: 'left', sortable: false, width: '20%' },
        { text: this.$t('confirmed'), value: 'status', align: 'center', sortable: false },
        { text: this.$t('location'), value: '', align: 'left', sortable: false },
        { text: this.$t('note'), value: 'note', align: 'left', sortable: false, width: '15%' }
      ],
      showSuccessAlert: false,
      auditRecord: {},
      showUnmatched: false,
      showConfirmed: false,
      isLoading: false,
      alertMsg: '',
      alertMsgArr: [this.$t('saveSuccess'), this.$t('syncInventorySuccess')],
      unsubscript: null,
      lastUpdated: new Date().toLocaleString()
    }
  },
  async mounted () {
    this.auditRecord = cloneDeep(this.initRecord)
    if (!this.isReadOnly) {
      console.log('build realtime')
      let successFunc = (doc) => {
        // check unsaved changes
        let changesMap = {}
        this.initRecord.upcs.forEach((item, index) => {
          let auditRecordItem = this.auditRecord.upcs[index]
          if (item.upc === auditRecordItem.upc && item.auditQty !== auditRecordItem.auditQty) {
            changesMap[item.upc] = auditRecordItem.auditQty
          }
        })
        let auditObj = doc.data()
        this.initRecord.upcs = auditObj && auditObj.upcs
        let newAuditRecord = cloneDeep(this.initRecord)
        newAuditRecord.upcs = newAuditRecord.upcs.map(item => {
          // keep the new unsaved changes
          if (!item.isConfirmed && changesMap[item.upc]) {
            item.auditQty = changesMap[item.upc]
          }
          return item
        })
        this.auditRecord = newAuditRecord
        this.lastUpdated = new Date().toLocaleString()
      }
      
      let errorFunc = (err) => {
        console.error('get audit RT failed', err)
      }

      this.unsubscript = await this.$store.dispatch('getOneAuditRecordRT', {successFunc, errorFunc, key: this.auditRecord._key})
    }
  },
  beforeDestroy () {
    console.log('unsubscript audit')
    this.unsubscript && this.unsubscript()
    this.unsubscript = null
  },
  computed: {
    items () {
      let rtn = this.auditRecord.upcs || []
      rtn.forEach(item => {
        if (this.upcToProductMap[item.upc]) {
          item.keywordString = this.upcToProductMap[item.upc].description || ''
        }
      })
      if (this.showUnmatched) {
        rtn = rtn.filter(item => item.qty !== item.auditQty || item.abnormalQty !== item.abnormalAuditQty)
      }
      if (this.showConfirmed) {
        rtn = rtn.filter(item => item.isConfirmed)
      }
      return rtn
    },
    isReadOnly () {
      return this.auditRecord.status === 'done'
    },
    upcToProductMap () {
      return this.$store.getters.upcMap
    }
  },
  methods: {
    exportAuditProducts () {
      const filename = `Audit-Products-${toPickerDateString(new Date())}`
      const content = []
      content.push(['UPS', 'Product Name', 'Quantity', 'Audit Normal Qty', 'Abnormal Audit Qty', 'Quantity', 'Confirmed', 'Note'])
      this.items.forEach(item => {
        content.push([item.upc, item.keywordString, item.qty, item.auditQty, item.abnormalAuditQty, item.isConfirmed, item.note])
      })
      const book = xlsx.utils.book_new()
      const sheet = xlsx.utils.aoa_to_sheet(content)
      xlsx.utils.book_append_sheet(book, sheet, 'sheet1')
      xlsx.writeFile(book, `${filename}.xlsx`)
    },
    async onSubmitted () {
      this.showSuccessAlert = false
      let {status, newUpcs} = await this.$store.dispatch('saveAudit', {upcs: this.auditRecord.upcs || [], _key: this.auditRecord._key, initUpcs: this.initRecord.upcs})
      if (status === 'reverse') {
        this.auditRecord.upcs = this.initRecord.upcs
      } else {
        this.auditRecord.upcs = newUpcs
        this.alertMsg = this.alertMsgArr[0]
        this.showSuccessAlert = true
      }
      this.$emit('onSubmitted', {target: this.initRecord, upcs: cloneDeep(this.auditRecord.upcs) || []})
      return 'keepPopup'
    },
    getTextClass (auditQty, qty, isConfirmed) {
      // if (!isConfirmed) return 'table-item'
      if (auditQty > qty) {
        return 'auditDiff'
      }
      // if (auditQty === qty) {
      //   return 'table-item'
      // }
      return 'auditDiffNeg'
    },
    getTextStyle (auditQty, qty, isConfirmed) {
      if (!isConfirmed) return ''
      if (auditQty > qty) {
        return 'color: green;'
      }
      if (auditQty === qty) {
        return ''
      }
      return 'color: red;'
    },
    async syncInventory () {
      if (!confirm(this.$t('confirmContinue'))) return 'skip'
      this.showSuccessAlert = false
      const {upcs} = await this.$store.dispatch('syncAuditInventory', {siteKey: this.initRecord.siteKey, auditRecord: this.auditRecord}) 
      this.auditRecord.upcs = upcs
      this.alertMsg = this.alertMsgArr[1]
      this.showSuccessAlert = true
    }
  },
  props: {
    value: Boolean,
    initRecord: Object
  }
}
</script>

<style>
.table-item {
  padding-bottom: 12px;
}

.text-red.theme--light.v-input:not(.v-input--is-disabled) input {
  color: red;
}

.text-green.theme--light.v-input:not(.v-input--is-disabled) input {
  color: green;
}

.auditDiff {
  padding: 3px 3px;
  color: white;
  background-color: #224855;
  text-align: end;
}

.auditDiffNeg {
  padding: 3px 3px;
  color: white;
  background-color: red;
  text-align: end;
}
</style>



// WEBPACK FOOTER //
// src/components/WarehouseAuditEditPopup.vue